// components/finance/RevenueGoal.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { usePagePreferences } from '../../../hooks/useUserPreference';
import { formatNumber } from '../../../utils';
import { GoalChart } from '../../common/GoalChart';

const REVENUE_CONFIG = {
  colors: {
    currentValue: '#B0BC22',
    remainingToGoal: '#DCDEEF',
    surplus: '#FFDD00'
  },
  text: {
    currentValue: 'Revenue YTD',
    remainingToGoal: 'Remaining To Goal',
    surplus: 'Surplus',
    buttonName: 'Edit Goal',
    goalTitle: 'Revenue Goal',
    goalDescription: 'Track your progress towards the revenue goal.'
  }
};

export const RevenueGoal = ({ 
  currentRevenue,
  goalAmount: initialGoalAmount = 1250000,
  onGoalChange,
  selectedYear = new Date().getFullYear(),
  CONFIG = REVENUE_CONFIG
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newGoal, setNewGoal] = useState(initialGoalAmount);
  const [isSaving, setIsSaving] = useState(false);

  const [preferences, setPreference, , prefsLoading, prefsError, prefsInitialized] = usePagePreferences(
    'finance_goals',
    { revenue_goals_by_year: {} }
  );

  const goalYearly = useMemo(() => {
    if (!prefsInitialized) return initialGoalAmount;
    const yearKey = String(selectedYear);
    return preferences.revenue_goals_by_year?.[yearKey] ?? initialGoalAmount;
  }, [prefsInitialized, selectedYear, preferences.revenue_goals_by_year, initialGoalAmount]);

  useEffect(() => {
    if (prefsInitialized) {
      setNewGoal(goalYearly);
    }
  }, [prefsInitialized, goalYearly]);

  const handleSaveGoal = async () => {
    setIsSaving(true);
    try {
      const yearKey = String(selectedYear);
      const updatedGoals = {
        ...(preferences.revenue_goals_by_year || {}),
        [yearKey]: newGoal
      };
      await setPreference('revenue_goals_by_year', updatedGoals);
      if (onGoalChange) onGoalChange(newGoal);
      setIsEditing(false);
    } catch (error) {
      console.error("❌ Error updating goal:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setNewGoal(goalYearly);
    setIsEditing(false);
  };

  const current = currentRevenue || 0;
  const goal = isEditing ? newGoal : goalYearly;
  const percentage = goal > 0 ? (current / goal) * 100 : 0;
  const surplus = Math.max(current - goal, 0);

  if (prefsLoading && !prefsInitialized) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 sm:p-6">
        <div className="flex justify-center items-center h-40">
          <div className="text-gray-500 text-sm">Loading goal...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      {/* Header with Progress Bar */}
      <div className="responsive-padding bg-gradient-to-r from-white to-gray-50 rounded-t-lg">
        <GoalChart
          currentValue={current}
          goalValue={goal}
          formatValue={(val) => `$${formatNumber(val)}`}
          config={CONFIG}
          isEditing={isEditing}
          onEditClick={() => setIsEditing(true)}
          onSave={handleSaveGoal}
          onCancel={handleCancelEdit}
          editValue={newGoal}
          onEditValueChange={(val) => setNewGoal(Number(val))}
          isSaving={isSaving}
          inputPlaceholder="Enter goal"
          inputProps={{ min: 0 }}
        />
      </div>

      {/* Card Body*/}
      <div className="responsive-padding-x responsive-padding-bottom pt-0">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
          {CONFIG.text.goalTitle} ({selectedYear})
        </h3>
        <p className="text-xs sm:text-sm text-gray-600">
          {CONFIG.text.goalDescription}
        </p>
        
        {/* Summary Stats - RESPONSIVE GRID */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-4 pt-4 border-t border-gray-200">
          <div className="text-center">
            <div className="text-base sm:text-lg font-semibold text-green-600">
              ${formatNumber(current)}
            </div>
            <div className="text-[10px] sm:text-xs text-gray-500 mt-0.5">
              Current Revenue
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-base sm:text-lg font-semibold text-gray-900">
              ${formatNumber(goal)}
            </div>
            <div className="text-[10px] sm:text-xs text-gray-500 mt-0.5">
              Annual Goal
            </div>
          </div>
          
          <div className="text-center">
            <div className={`text-base sm:text-lg font-semibold ${
              surplus > 0 ? 'text-yellow-600' : 'text-gray-600'
            }`}>
              {formatNumber(percentage)}%
            </div>
            <div className="text-[10px] sm:text-xs text-gray-500 mt-0.5">
              Progress
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};